<script setup lang="ts">
import type { RegionContentProps } from '.';
import type Carousel from '@/components/ui/carousel/Carousel.vue';

import { Container } from '@/components/Container';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import {
	regionGridContentVariants,
	regionGridImageGalleryVariants,
	regionWrapperVariants
} from '.';

const props = withDefaults(defineProps<RegionContentProps>(), {
	class: ''
});

type Variant = 'default' | 'reverse';

const variant = computed<Variant>(() =>
	(props.contentIndex || 0) % 2 === 0 ? 'default' : 'reverse'
);

const notEmptyImages = computed(() =>
	props.images.filter(image => image.url !== 'empty')
);

function getImageGalleryVariant(imageIndex: number): Variant {
	if (imageIndex !== 0 || variant.value === 'default') return 'default';

	return 'reverse';
}

const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);
</script>

<!-- eslint-disable vue/no-v-html -->
<template>
	<Container
		:class="cn(regionWrapperVariants({ variant }), props.class)"
		class="px-0"
	>
		<div class="grid grid-cols-2 gap-6">
			<div
				:class="cn(regionGridContentVariants({ variant }), info.class)"
				class="px-5 md:px-0"
			>
				<span
					class="font-matter-sq text-2xl leading-[150%] tracking-[-0.03rem] text-primary lg:text-4xl lg:tracking-[-0.045rem]"
				>
					{{ info.title }}
				</span>

				<p
					class="text-base font-normal leading-[175%] text-primary"
					v-html="info.content"
				/>
			</div>
			<div
				v-for="(image, index) in images"
				:key="`${info.title}-image-${index}`"
				:class="
					cn(
						'relative hidden h-full items-end lg:flex',
						image.class,
						regionGridImageGalleryVariants({
							variant: getImageGalleryVariant(index)
						})
					)
				"
			>
				<div v-if="image.url === 'empty'" class="h-[17.5rem] w-full" />

				<template v-else>
					<NuxtImg
						format="webp"
						:src="image.url"
						:alt="`${info.title} Imagem ${index}`"
						sizes="400px md:60vw"
						class="h-[17.5rem] w-full object-cover object-center"
						loading="lazy"
					/>
					<span
						v-if="image.credits"
						class="absolute bottom-2 left-2 flex select-none items-center justify-start text-xs text-white"
					>
						&#169; {{ image.credits }}
					</span>
				</template>
			</div>

			<Carousel
				ref="carouselContainerRef"
				class="relative col-span-2 w-full lg:hidden"
				:plugins="[WheelGesturesPlugin()]"
				:opts="{ align: 'start', skipSnaps: true }"
			>
				<CarouselContent class="gap-6">
					<CarouselItem
						v-for="(image, index) in notEmptyImages"
						:key="`${info.title}-mobile-image-${index}`"
						class="h-[17.5rem] max-w-[19.4375rem] gap-6"
						:class="{
							'pl-6': index === 0,
							'pr-6': index === notEmptyImages.length - 1
						}"
					>
						<NuxtImg
							format="webp"
							:placeholder="index > 0"
							:src="image.url"
							:alt="`${info.title} Imagem ${index}`"
							class="size-full object-cover object-center"
							loading="lazy"
						/>
					</CarouselItem>
				</CarouselContent>
			</Carousel>
		</div>
	</Container>
</template>
